import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FaBriefcase,
  FaChevronDown,
  FaCrosshairs,
  FaHome,
  FaHotel,
  FaKeyboard,
  FaLink,
  FaMapMarkedAlt,
  FaMapMarkerAlt,
  FaSearch,
} from 'react-icons/fa';
import { normalizeUserAddress } from '../../Cart/addressesApi';
import { addressFormToApiPayload, createAddressApiClient } from './addressApiClient';
import {
  canAddSavedAddress,
  demoteExtraPrimaryAddresses,
  MAX_SAVED_ADDRESSES,
  resolveSavedAddressIdAfterSubmit,
  SAVED_ADDRESS_LIMIT_MESSAGE,
} from './addressManagementUtils';
import {
  cleanText,
  createLeafletMarkerIcon,
  DEFAULT_MAP_CENTER,
  draftFromCoords,
  EMPTY_FORM,
  findLatLngInText,
  geocodeResultToDraft,
  geocodeTextQuery,
  GOOGLE_MAPS_API_KEY,
  loadGoogleMaps,
  loadLeaflet,
  mapDraftToForm,
  parseMapUrlInput,
  reverseGeocodeCoords,
} from './addressFormModalUtils';
import './AccountAddresses.css';

const ADDRESS_TYPES = [
  { value: 0, label: 'Home', icon: FaHome },
  { value: 1, label: 'Work', icon: FaBriefcase },
  { value: 2, label: 'Hotel', icon: FaHotel },
  { value: 3, label: 'Other', icon: FaMapMarkerAlt },
];

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSaved: (address: ReturnType<typeof normalizeUserAddress>) => void,
 *   editAddress?: object | null,
 *   addressesCount?: number,
 *   maxAddresses?: number | null,
 *   saveLabel?: string,
 * }} props
 */
const AddressFormModal = ({
  open,
  onClose,
  onSaved,
  editAddress = null,
  addressesCount = 0,
  maxAddresses = MAX_SAVED_ADDRESSES,
  saveLabel,
}) => {
  const requestAddressApi = useMemo(() => createAddressApiClient(), []);
  const editing = editAddress?.id != null;

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [coordInput, setCoordInput] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [mapStatus, setMapStatus] = useState('idle');
  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);
  const [modalStep, setModalStep] = useState('location');

  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);
  const leafletNodeRef = useRef(null);
  const leafletMapRef = useRef(null);
  const leafletMarkerRef = useRef(null);
  const wasOpenRef = useRef(false);

  const resetMapRefs = () => {
    mapRef.current = null;
    markerRef.current = null;
    autocompleteRef.current = null;
    if (leafletMapRef.current) {
      try {
        leafletMapRef.current.remove();
      } catch {
        // ignore teardown errors
      }
      leafletMapRef.current = null;
    }
    leafletMarkerRef.current = null;
  };

  const resetFormState = useCallback(() => {
    setForm({ ...EMPTY_FORM, isPrimary: addressesCount === 0 });
    setErrors({});
    setCandidate(null);
    setLocationInput('');
    setUrlInput('');
    setCoordInput('');
    setAdvancedOpen(false);
    setError('');
    setModalStep('location');
    setMapCenter(DEFAULT_MAP_CENTER);
    resetMapRefs();
  }, [addressesCount]);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;

    resetMapRefs();
    if (editAddress?.id != null) {
      setForm({
        ...editAddress,
        latitude: editAddress.latitude ?? '',
        longitude: editAddress.longitude ?? '',
        country: editAddress.country || 'India',
      });
      setCandidate(null);
      setModalStep('details');
      setMapCenter({
        lat: Number(editAddress.latitude) || DEFAULT_MAP_CENTER.lat,
        lng: Number(editAddress.longitude) || DEFAULT_MAP_CENTER.lng,
      });
      setErrors({});
      setError('');
    } else {
      resetFormState();
    }
  }, [open, editAddress, resetFormState]);

  const reverseGeocode = useCallback(
    async (coords) => reverseGeocodeCoords(coords, geocoderRef.current),
    [],
  );

  const geocodeText = useCallback(
    async (text) => geocodeTextQuery(text, geocoderRef.current),
    [],
  );

  const resolveDraft = useCallback(
    async (draft, sourceLabel, { recenter = true } = {}) => {
      const nextDraft =
        !draft.formattedAddress && Number.isFinite(draft.latitude) && Number.isFinite(draft.longitude)
          ? await reverseGeocode({ lat: draft.latitude, lng: draft.longitude })
          : draft;
      setError('');
      if (recenter) setMapCenter({ lat: nextDraft.latitude, lng: nextDraft.longitude });
      setCandidate({ ...nextDraft, sourceLabel });
    },
    [reverseGeocode],
  );

  useEffect(() => {
    if (modalStep !== 'location' || !open || !GOOGLE_MAPS_API_KEY || !mapNodeRef.current) return undefined;
    let cancelled = false;

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapNodeRef.current) return;
        setMapStatus('ready');
        const center = {
          lat: Number(form.latitude) || mapCenter.lat,
          lng: Number(form.longitude) || mapCenter.lng,
        };

        if (!mapRef.current) {
          mapRef.current = new maps.Map(mapNodeRef.current, {
            center,
            zoom: Number(form.latitude) && Number(form.longitude) ? 16 : 12,
            gestureHandling: 'greedy',
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          });
          markerRef.current = new maps.Marker({
            position: center,
            map: mapRef.current,
            draggable: true,
          });
          geocoderRef.current = new maps.Geocoder();

          mapRef.current.addListener('click', async (event) => {
            const coords = { lat: event.latLng.lat(), lng: event.latLng.lng() };
            markerRef.current.setPosition(coords);
            setResolving(true);
            try {
              const draft = await reverseGeocode(coords);
              await resolveDraft(draft, 'Picked on map', { recenter: false });
            } catch (err) {
              setError(err?.message || 'Could not read this map location.');
            } finally {
              setResolving(false);
            }
          });

          markerRef.current.addListener('dragend', async (event) => {
            const coords = { lat: event.latLng.lat(), lng: event.latLng.lng() };
            setResolving(true);
            try {
              const draft = await reverseGeocode(coords);
              await resolveDraft(draft, 'Marker position', { recenter: false });
            } catch (err) {
              setError(err?.message || 'Could not read this map location.');
            } finally {
              setResolving(false);
            }
          });
        } else {
          mapRef.current.setCenter(center);
          markerRef.current?.setPosition(center);
        }
      })
      .catch(() => setMapStatus('failed'));

    return () => {
      cancelled = true;
    };
  }, [
    form.latitude,
    form.longitude,
    open,
    mapCenter.lat,
    mapCenter.lng,
    modalStep,
    resolveDraft,
    reverseGeocode,
  ]);

  useEffect(() => {
    if (GOOGLE_MAPS_API_KEY || modalStep !== 'location' || !open || !leafletNodeRef.current) {
      return undefined;
    }
    let cancelled = false;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !leafletNodeRef.current) return;
        setMapStatus('ready');
        const center = [
          Number(form.latitude) || mapCenter.lat,
          Number(form.longitude) || mapCenter.lng,
        ];

        if (!leafletMapRef.current) {
          const map = L.map(leafletNodeRef.current, { zoomControl: true }).setView(
            center,
            Number(form.latitude) && Number(form.longitude) ? 16 : 13,
          );
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap contributors',
          }).addTo(map);
          const marker = L.marker(center, {
            draggable: true,
            icon: createLeafletMarkerIcon(L),
          }).addTo(map);
          leafletMapRef.current = map;
          leafletMarkerRef.current = marker;

          const handlePick = async (latlng) => {
            leafletMarkerRef.current?.setLatLng(latlng);
            setResolving(true);
            try {
              const draft = await reverseGeocode({ lat: latlng.lat, lng: latlng.lng });
              await resolveDraft(draft, 'Picked on map', { recenter: false });
            } catch (err) {
              setError(err?.message || 'Could not read this map location.');
            } finally {
              setResolving(false);
            }
          };

          map.on('click', (event) => handlePick(event.latlng));
          marker.on('dragend', () => handlePick(marker.getLatLng()));
          setTimeout(() => leafletMapRef.current?.invalidateSize(), 0);
        } else {
          leafletMapRef.current.setView(center);
          leafletMarkerRef.current?.setLatLng(center);
          setTimeout(() => leafletMapRef.current?.invalidateSize(), 0);
        }
      })
      .catch(() => setMapStatus('failed'));

    return () => {
      cancelled = true;
    };
  }, [
    form.latitude,
    form.longitude,
    open,
    mapCenter.lat,
    mapCenter.lng,
    modalStep,
    resolveDraft,
    reverseGeocode,
  ]);

  useEffect(() => {
    if (modalStep !== 'location' || !open || !GOOGLE_MAPS_API_KEY || !searchInputRef.current) {
      return undefined;
    }
    let cancelled = false;

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !searchInputRef.current) return;
        autocompleteRef.current = null;
        autocompleteRef.current = new maps.places.Autocomplete(searchInputRef.current, {
          fields: ['address_components', 'formatted_address', 'geometry', 'name'],
          componentRestrictions: { country: 'in' },
        });
        autocompleteRef.current.addListener('place_changed', async () => {
          const place = autocompleteRef.current.getPlace();
          if (!place?.geometry?.location) return;
          const draft = geocodeResultToDraft(place);
          await resolveDraft(draft, 'Google place');
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [open, modalStep, resolveDraft]);

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Please enter a name for this address.';
    if (!form.address.trim()) next.address = 'House, flat, or street details are required.';
    if (!form.city.trim()) next.city = 'City is required.';
    if (!form.state.trim()) next.state = 'State is required.';
    if (!form.country.trim()) next.country = 'Country is required.';
    if (!form.postalCode.trim()) next.postalCode = 'Postal code is required.';
    if (!Number.isFinite(Number(form.latitude))) next.latitude = 'Enter a valid latitude.';
    if (!Number.isFinite(Number(form.longitude))) next.longitude = 'Enter a valid longitude.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const updateForm = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const applyCandidate = () => {
    if (!candidate) return;
    setForm((prev) => mapDraftToForm(candidate, prev, addressesCount));
    setCandidate(null);
    setModalStep('details');
    resetMapRefs();
  };

  const resolveSearch = async () => {
    const text = cleanText(locationInput);
    if (!text) {
      setError('Type your address or area to search.');
      return;
    }
    setResolving(true);
    setError('');
    try {
      const draft = await geocodeText(text);
      await resolveDraft(draft, 'Address search');
    } catch {
      setError(
        "We couldn't pinpoint that address. Drag the pin on the map to your exact spot, or tap “Use my current location”.",
      );
    } finally {
      setResolving(false);
    }
  };

  const resolveUrl = async () => {
    const parsed = parseMapUrlInput(urlInput);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    if (!parsed.coords && !parsed.query) {
      setError('Paste a Google Maps link or location text.');
      return;
    }
    setResolving(true);
    setError('');
    try {
      const draft = parsed.coords ? draftFromCoords(parsed.coords) : await geocodeText(parsed.query);
      await resolveDraft(draft, parsed.coords ? 'Google Maps URL coordinates' : 'Google Maps URL');
    } catch (err) {
      setError(err?.message || 'Could not read that Google Maps location.');
    } finally {
      setResolving(false);
    }
  };

  const resolveCoords = async () => {
    const coords = findLatLngInText(coordInput);
    if (!coords) {
      setError('Enter latitude and longitude like 17.493000, 78.391000.');
      return;
    }
    setResolving(true);
    setError('');
    try {
      await resolveDraft(draftFromCoords(coords), 'Coordinates');
    } catch (err) {
      setError(err?.message || 'Could not read these coordinates.');
    } finally {
      setResolving(false);
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Location services are not supported in this browser.');
      return;
    }
    setResolving(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        try {
          await resolveDraft(draftFromCoords(coords), 'Current location');
        } catch (err) {
          setError(err?.message || 'Could not read your location address.');
        } finally {
          setResolving(false);
        }
      },
      () => {
        setResolving(false);
        setError('Could not read your location. You can search, paste a map URL, or enter coordinates.');
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 },
    );
  };

  const goToLocationStep = () => {
    resetMapRefs();
    setModalStep('location');
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  const saveAddress = async () => {
    if (!validate()) return;
    if (!editing && maxAddresses != null && !canAddSavedAddress(addressesCount)) {
      setError(SAVED_ADDRESS_LIMIT_MESSAGE);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = addressFormToApiPayload(form, { includeId: editing });
      await requestAddressApi(editing ? '/auth/updateUserAddress' : '/auth/addUserAddress', {
        method: 'POST',
        body: payload,
      });

      if (payload.isPrimary) {
        const refreshed = await requestAddressApi('/auth/getUserAddress');
        const list = Array.isArray(refreshed?.data)
          ? refreshed.data.map(normalizeUserAddress)
          : [];
        const keepId = resolveSavedAddressIdAfterSubmit(list, form, editing);
        if (keepId != null) {
          await demoteExtraPrimaryAddresses(requestAddressApi, list, keepId);
        }
      }

      const data = await requestAddressApi('/auth/getUserAddress');
      const list = Array.isArray(data?.data) ? data.data.map(normalizeUserAddress) : [];
      const savedId = resolveSavedAddressIdAfterSubmit(list, form, editing);
      const savedRow =
        list.find((a) => Number(a.id) === Number(savedId)) || list[list.length - 1];
      if (!savedRow) throw new Error('Address saved but could not be loaded.');
      const lat = Number(form.latitude);
      const lng = Number(form.longitude);
      const saved = {
        ...savedRow,
        latitude: Number.isFinite(savedRow.latitude) ? savedRow.latitude : lat,
        longitude: Number.isFinite(savedRow.longitude) ? savedRow.longitude : lng,
      };
      onSaved(saved);
      resetFormState();
    } catch (err) {
      setError(err?.message || (editing ? 'Could not update address.' : 'Could not save address.'));
    } finally {
      setSaving(false);
    }
  };

  const previewAddress = [
    candidate?.address,
    candidate?.city,
    candidate?.state,
    candidate?.postalCode,
    candidate?.country,
  ]
    .filter(Boolean)
    .join(', ');

  if (!open) return null;

  const submitButtonLabel = saving
    ? 'Saving...'
    : saveLabel || (editing ? 'Update address' : 'Save address');

  const modalTree = (
    <div
      className="account-addresses__modal"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <section
        className="account-addresses__creator"
        role="dialog"
        aria-modal="true"
        aria-label={editing ? 'Edit address' : 'Add new address'}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="account-addresses__creator-head">
          <div>
            <h2>{editing ? 'Edit delivery location' : 'Add delivery location'}</h2>
            <span>
              {modalStep === 'location'
                ? 'Step 1 of 2: find your spot, check the pin, then tap “Use this location”.'
                : 'Step 2 of 2: review the details and save.'}
            </span>
          </div>
          <button type="button" onClick={handleClose}>
            Close
          </button>
        </div>

        {error ? <div className="account-addresses__modal-error">{error}</div> : null}

        {modalStep === 'location' ? (
          <>
            <div className="account-addresses__location-panel">
              <div className="account-addresses__stepper" aria-label="Address steps">
                <div
                  className={`account-addresses__step${modalStep === 'location' ? ' account-addresses__step--active' : ''}`}
                >
                  <span>1</span>
                  Location
                </div>
                <div
                  className={`account-addresses__step${modalStep === 'details' ? ' account-addresses__step--active' : ''}`}
                >
                  <span>2</span>
                  Details
                </div>
              </div>

              <div className="account-addresses__resolver">
                <div className="account-addresses__resolver-row">
                  <div className="account-addresses__resolver-field">
                    <label htmlFor="address-search-input">Search your address or area</label>
                    <input
                      id="address-search-input"
                      ref={searchInputRef}
                      value={locationInput}
                      onChange={(event) => setLocationInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          resolveSearch();
                        }
                      }}
                      placeholder="Type your address, e.g. Kondapur, Hyderabad"
                    />
                  </div>
                  <button type="button" onClick={resolveSearch} disabled={resolving}>
                    <FaSearch aria-hidden />
                    {resolving ? 'Finding...' : 'Find'}
                  </button>
                </div>

                <button
                  type="button"
                  className="account-addresses__current-location"
                  onClick={useCurrentLocation}
                  disabled={resolving}
                >
                  <FaCrosshairs aria-hidden />
                  Use my current location
                </button>

                <button
                  type="button"
                  className={`account-addresses__advanced-toggle${advancedOpen ? ' account-addresses__advanced-toggle--open' : ''}`}
                  aria-expanded={advancedOpen}
                  onClick={() => setAdvancedOpen((open) => !open)}
                >
                  <FaChevronDown aria-hidden />
                  Enter coordinates or paste a Google Maps link
                </button>

                {advancedOpen ? (
                  <div className="account-addresses__advanced">
                    <div className="account-addresses__resolver-row">
                      <div className="account-addresses__resolver-field">
                        <label htmlFor="address-url-input">Google Maps link</label>
                        <input
                          id="address-url-input"
                          value={urlInput}
                          onChange={(event) => setUrlInput(event.target.value)}
                          placeholder="https://www.google.com/maps/place/..."
                        />
                      </div>
                      <button type="button" onClick={resolveUrl} disabled={resolving}>
                        <FaLink aria-hidden />
                        {resolving ? 'Reading...' : 'Use link'}
                      </button>
                    </div>

                    <div className="account-addresses__resolver-row">
                      <div className="account-addresses__resolver-field">
                        <label htmlFor="address-coords-input">Latitude, Longitude</label>
                        <input
                          id="address-coords-input"
                          value={coordInput}
                          onChange={(event) => setCoordInput(event.target.value)}
                          placeholder="17.493000, 78.391000"
                          inputMode="decimal"
                        />
                      </div>
                      <button type="button" onClick={resolveCoords} disabled={resolving}>
                        <FaKeyboard aria-hidden />
                        {resolving ? 'Checking...' : 'Check'}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="account-addresses__map-shell">
              {GOOGLE_MAPS_API_KEY ? (
                <>
                  <div ref={mapNodeRef} className="account-addresses__map" aria-label="Google map picker" />
                  {mapStatus === 'failed' ? (
                    <div className="account-addresses__map-overlay">
                      Google Maps could not load. Search or enter coordinates to continue.
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <div ref={leafletNodeRef} className="account-addresses__map" aria-label="Map picker" />
                  {mapStatus === 'failed' ? (
                    <div className="account-addresses__map-overlay">
                      The map could not load. Search or enter coordinates to continue.
                    </div>
                  ) : null}
                </>
              )}
              {candidate ? (
                <div className="account-addresses__candidate account-addresses__candidate--floating">
                  <div>
                    <span>{candidate.sourceLabel}</span>
                    <strong>{previewAddress || candidate.formattedAddress || 'Selected location'}</strong>
                    <small>
                      {candidate.latitude.toFixed(6)}, {candidate.longitude.toFixed(6)}
                    </small>
                  </div>
                  <button type="button" onClick={applyCandidate}>
                    Use this location
                  </button>
                </div>
              ) : null}
            </div>

            <p className="account-addresses__map-hint">
              <FaMapMarkedAlt aria-hidden />
              Zoom and pan the map, then click or drag the pin to your exact spot and confirm.
            </p>
          </>
        ) : null}

        {modalStep === 'details' ? (
          <div className="auth-form account-addresses__form" role="group" aria-label="Delivery details">
            <div className="account-addresses__form-header">
              <h3>Delivery details</h3>
              <p>Review the confirmed location, adjust the final delivery details, then save.</p>
            </div>

            <div className="account-addresses__type-row" role="radiogroup" aria-label="Address type">
              {ADDRESS_TYPES.map((item) => {
                const Icon = item.icon;
                const active = Number(form.type) === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    className={`account-addresses__type${active ? ' account-addresses__type--active' : ''}`}
                    onClick={() => setForm((prev) => ({ ...prev, type: item.value }))}
                    aria-pressed={active}
                  >
                    <Icon aria-hidden />
                    {item.label}
                  </button>
                );
              })}
            </div>

            <div className="account-addresses__details-grid">
              <label className="account-addresses__details-field">
                <span>Address label *</span>
                <input
                  name="name"
                  value={form.name}
                  onChange={updateForm}
                  placeholder="Home, Office, Rahul's flat"
                  autoComplete="name"
                  className={errors.name ? 'account-page__input--invalid' : ''}
                />
                {errors.name ? <p className="account-page__field-error">{errors.name}</p> : null}
              </label>

              <label className="account-addresses__details-field account-addresses__details-field--wide">
                <span>House / Flat / Floor *</span>
                <input
                  name="address"
                  value={form.address}
                  onChange={updateForm}
                  autoComplete="street-address"
                  className={errors.address ? 'account-page__input--invalid' : ''}
                />
                {errors.address ? <p className="account-page__field-error">{errors.address}</p> : null}
              </label>

              <label className="account-addresses__details-field">
                <span>City *</span>
                <input
                  name="city"
                  value={form.city}
                  onChange={updateForm}
                  autoComplete="address-level2"
                  className={errors.city ? 'account-page__input--invalid' : ''}
                />
                {errors.city ? <p className="account-page__field-error">{errors.city}</p> : null}
              </label>

              <label className="account-addresses__details-field">
                <span>State *</span>
                <input
                  name="state"
                  value={form.state}
                  onChange={updateForm}
                  autoComplete="address-level1"
                  className={errors.state ? 'account-page__input--invalid' : ''}
                />
                {errors.state ? <p className="account-page__field-error">{errors.state}</p> : null}
              </label>

              <label className="account-addresses__details-field">
                <span>Country *</span>
                <input
                  name="country"
                  value={form.country}
                  onChange={updateForm}
                  autoComplete="country-name"
                  className={errors.country ? 'account-page__input--invalid' : ''}
                />
                {errors.country ? <p className="account-page__field-error">{errors.country}</p> : null}
              </label>

              <label className="account-addresses__details-field">
                <span>Postal code *</span>
                <input
                  name="postalCode"
                  value={form.postalCode}
                  onChange={updateForm}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  className={errors.postalCode ? 'account-page__input--invalid' : ''}
                />
                {errors.postalCode ? (
                  <p className="account-page__field-error">{errors.postalCode}</p>
                ) : null}
              </label>

              <div className="account-addresses__coords-readout account-addresses__details-field--wide">
                <span>Confirmed pin</span>
                <strong>
                  {form.latitude && form.longitude
                    ? `${form.latitude}, ${form.longitude}`
                    : 'Go back and confirm a location first'}
                </strong>
              </div>
            </div>

            {errors.latitude || errors.longitude ? (
              <p className="account-page__field-error">{errors.latitude || errors.longitude}</p>
            ) : null}

            <div className="account-addresses__form-options">
              <label className="account-addresses__checkbox">
                <input type="checkbox" name="isPrimary" checked={form.isPrimary} onChange={updateForm} />
                Set as default address
              </label>
              <p className="account-addresses__checkbox-hint">
                Only one default is kept. Saving will clear default on your other addresses.
              </p>
            </div>

            <div className="account-addresses__modal-actions">
              <button type="button" className="account-addresses__back-btn" onClick={goToLocationStep}>
                Back to location
              </button>
              <button
                type="button"
                className="auth-form__submit"
                disabled={saving}
                onClick={saveAddress}
              >
                {submitButtonLabel}
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );

  return createPortal(modalTree, document.body);
};

export default AddressFormModal;
